import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "menu", "selection", "hiddenId", "hiddenNewName", "subtitleInput"]
  static values = {
    categories: Array,
    initialId: Number,
    initialName: String,
    initialSubtitle: String
  }

  connect() {
    this.filtered = []
    this.activeIndex = 0
    this.selected = null
    this.closeTimer = null

    if (this.hasInitialIdValue && this.hasInitialNameValue && this.initialIdValue > 0 && this.initialNameValue.trim().length > 0) {
      this.selected = {
        id: this.initialIdValue,
        name: this.initialNameValue,
        subtitle: this.hasInitialSubtitleValue ? this.initialSubtitleValue : "",
        isNew: false
      }
      this.hiddenIdTarget.value = this.initialIdValue
      this.hiddenNewNameTarget.value = ""
      this.updateSubtitleInput(this.selected.subtitle)
    }

    this.renderSelection()
    this.renderMenu()
  }

  open() {
    this.menuTarget.classList.remove("hidden")
    this.filter()
  }

  close() {
    this.menuTarget.classList.add("hidden")
    this.activeIndex = 0
  }

  scheduleClose() {
    this.closeTimer = window.setTimeout(() => this.close(), 120)
  }

  cancelClose() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer)
      this.closeTimer = null
    }
  }

  filter() {
    const term = this.inputTarget.value.trim().toLowerCase()
    this.filtered = this.categoriesValue.filter((category) => category.name.toLowerCase().includes(term))
    this.activeIndex = 0
    this.renderMenu()
  }

  keydown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.moveActive(1)
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      this.moveActive(-1)
      return
    }

    if (event.key !== "Enter") {
      return
    }

    event.preventDefault()

    const items = this.menuItems()
    if (items.length === 0) {
      return
    }

    const item = items[this.activeIndex] || items[0]
    this.activateItem(item)
  }

  selectFromClick(event) {
    event.preventDefault()
    this.activateItem(event.currentTarget)
  }

  clearSelection(event) {
    event.preventDefault()
    this.selected = null
    this.hiddenIdTarget.value = ""
    this.hiddenNewNameTarget.value = ""
    this.updateSubtitleInput("")
    this.renderSelection()
    this.inputTarget.value = ""
    this.inputTarget.focus()
    this.filter()
  }

  moveActive(step) {
    const items = this.menuItems()
    if (items.length === 0) {
      return
    }

    this.activeIndex = (this.activeIndex + step + items.length) % items.length
    this.highlightActiveItem()
  }

  activateItem(item) {
    const type = item.dataset.type
    const name = item.dataset.name
    const subtitle = item.dataset.subtitle || ""

    if (type === "create") {
      this.selected = { id: null, name, subtitle: "", isNew: true }
      this.hiddenIdTarget.value = ""
      this.hiddenNewNameTarget.value = name
      this.updateSubtitleInput("")
    } else {
      this.selected = { id: Number(item.dataset.id), name, subtitle, isNew: false }
      this.hiddenIdTarget.value = item.dataset.id
      this.hiddenNewNameTarget.value = ""
      this.updateSubtitleInput(subtitle)
    }

    this.renderSelection()
    this.inputTarget.value = ""
    this.close()
  }

  renderSelection() {
    if (!this.selected) {
      this.selectionTarget.innerHTML = "<span class=\"text-xs text-[var(--text-muted)]\">Nenhuma categoria selecionada.</span>"
      return
    }

    const badgeClass = this.selected.isNew ? "border-amber-200 bg-amber-50 text-amber-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"
    const prefix = this.selected.isNew ? "Nova categoria" : "Categoria"
    const safeName = this.escapeHtml(this.selected.name)

    this.selectionTarget.innerHTML = `
      <span class="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${badgeClass}">
        <span>${prefix}: ${safeName}</span>
        <button type="button" class="font-bold" data-action="category-select#clearSelection" aria-label="Remover categoria">x</button>
      </span>
    `
  }

  renderMenu() {
    const term = this.inputTarget.value.trim()
    const hasExactMatch = this.filtered.some((item) => item.name.toLowerCase() === term.toLowerCase())
    const items = []

    this.filtered.forEach((category) => {
      items.push(`
        <button
          type="button"
          data-action="mousedown->category-select#cancelClose click->category-select#selectFromClick"
          data-type="existing"
          data-id="${category.id}"
          data-name="${this.escapeHtml(category.name)}"
          data-subtitle="${this.escapeHtml(category.subtitle || "")}"
          class="category-option flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-[var(--text-strong)] hover:bg-slate-100"
        >
          <span>${this.escapeHtml(category.name)}</span>
          <span class="text-xs text-[var(--text-muted)]">existente</span>
        </button>
      `)
    })

    if (term.length > 0 && !hasExactMatch) {
      items.push(`
        <button
          type="button"
          data-action="mousedown->category-select#cancelClose click->category-select#selectFromClick"
          data-type="create"
          data-name="${this.escapeHtml(term)}"
          data-subtitle=""
          class="category-option flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-medium text-amber-700 hover:bg-amber-50"
        >
          <span>Criar categoria \"${this.escapeHtml(term)}\"</span>
          <span class="text-xs">nova</span>
        </button>
      `)
    }

    if (items.length === 0) {
      this.menuTarget.innerHTML = "<p class=\"px-3 py-2 text-sm text-[var(--text-muted)]\">Nenhuma categoria encontrada.</p>"
      return
    }

    this.menuTarget.innerHTML = items.join("")
    this.highlightActiveItem()
  }

  highlightActiveItem() {
    this.menuItems().forEach((item, index) => {
      if (index === this.activeIndex) {
        item.classList.add("bg-slate-100")
      } else {
        item.classList.remove("bg-slate-100")
      }
    })
  }

  menuItems() {
    return Array.from(this.menuTarget.querySelectorAll(".category-option"))
  }

  escapeHtml(value) {
    return (value || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }

  updateSubtitleInput(value) {
    if (!this.hasSubtitleInputTarget) {
      return
    }

    this.subtitleInputTarget.value = value || ""
  }
}
